import React, { useEffect, useMemo, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { createIntl, createIntlCache, RawIntlProvider, type IntlConfig } from 'react-intl';

import { organizationIdSelector } from '@/domain/env/envSelector';
import { useTextOverridesBundle, useTextOverridesLiveUpdates } from '@/domain/textOverrides/queries/bundle';
import { useEditMode } from '@/lib/i18nOverrides/editMode';
import { withDeferredOverrides, withMarkers } from '@/lib/i18nOverrides/intlWrappers';
import { mergeOverrides, type Messages } from '@/lib/i18nOverrides/mergeOverrides';
import { markerRegistry } from '@/lib/i18nOverrides/registry';
import { TextOverridesContext, type TextOverridesContextValue } from '@/components/TextOverrides/TextOverridesContext';

interface Props {
  readonly locale: string;
  readonly defaultLocale: string;
  readonly messages: Messages;
  readonly textComponent?: IntlConfig['textComponent'];
  readonly children?: ReactNode;
}

const NO_OVERRIDES: Messages = {};
const cache = createIntlCache();

/**
 * The app's root translation provider: static catalog ⊕ the effective org's overrides for the locale, guarded, and
 * — in text edit mode — every formatted text carries an invisible key marker. Replaces react-intl's IntlProvider
 * one to one (same locale, defaultLocale, textComponent); no call site changes.
 */
const OrgIntlProvider = ({ locale, defaultLocale, messages, textComponent, children }: Props) => {
  const rawOrgId: unknown = useSelector(organizationIdSelector);
  const orgId = typeof rawOrgId === 'string' && rawOrgId !== '' ? rawOrgId : null;
  const editMode = useEditMode();

  const { data: bundle } = useTextOverridesBundle(orgId, locale);
  useTextOverridesLiveUpdates(orgId, locale);

  // A late answer for the org the user just left must not apply to the new one.
  const overrides = bundle && bundle.orgId === orgId ? bundle.messages : NO_OVERRIDES;
  const merged = useMemo(() => mergeOverrides(messages, overrides), [messages, overrides]);

  useEffect(() => {
    if (merged.dropped.length > 0) console.warn('[text overrides] dropped incompatible overrides', merged.dropped);
  }, [merged.dropped]);

  const plainIntl = useMemo(
    () =>
      withDeferredOverrides(
        createIntl({ locale, defaultLocale, messages: merged.messages, textComponent }, cache),
        merged.deferred,
      ),
    [locale, defaultLocale, merged, textComponent],
  );
  const intl = useMemo(() => (editMode ? withMarkers(plainIntl, markerRegistry) : plainIntl), [plainIntl, editMode]);

  const context = useMemo<TextOverridesContextValue>(
    () => ({ plainIntl, baseMessages: messages, overrides, messages: merged.messages, orgId, locale }),
    [plainIntl, messages, overrides, merged.messages, orgId, locale],
  );

  return (
    <TextOverridesContext.Provider value={context}>
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </TextOverridesContext.Provider>
  );
};

export default OrgIntlProvider;
